<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { LetterState } from './types';

  export let letter: string = '';
  export let state: LetterState = 'unused';
  export let disabled: boolean = false;
  export let size: 'small' | 'medium' | 'large' = 'medium';

  const dispatch = createEventDispatcher();

  function handleClick() {
    if (!disabled && state === 'unused') {
      dispatch('select', letter);
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }

  $: cssClass = `tile tile-${state} tile-${size}`;
</script>

<button
  class={cssClass}
  on:click={handleClick}
  on:keydown={handleKeydown}
  disabled={disabled || state !== 'unused'}
  aria-label={letter.toUpperCase()}
  aria-pressed={state !== 'unused'}
  tabindex="0"
>
  {letter.toUpperCase()}
</button>

<style>
  .tile {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'SF Mono', 'Fira Code', 'JetBrains Mono', monospace;
    font-weight: 700;
    border: 2px solid var(--tile-border, #ddd);
    border-radius: 6px;
    background: var(--tile-bg, #f8f9fa);
    color: var(--tile-color, #2c3e50);
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    user-select: none;
    position: relative;
    overflow: hidden;
  }

  .tile::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, transparent 40%, rgba(255,255,255,0.3) 100%);
    pointer-events: none;
  }

  .tile:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: var(--tile-hover-border, #5a7d9a);
  }

  .tile:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  .tile:focus-visible {
    outline: 3px solid var(--focus-ring, #4a90d9);
    outline-offset: 2px;
  }

  .tile:disabled {
    cursor: default;
    opacity: 0.6;
  }

  /* States */
  .tile-unused {
    background: var(--tile-bg, #f8f9fa);
    border-color: var(--tile-border, #dee2e6);
    color: var(--tile-color, #2c3e50);
  }

  .tile-correct {
    background: var(--correct-bg, #27ae60);
    border-color: var(--correct-border, #219a52);
    color: white;
    animation: tilePop 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  }

  .tile-wrong {
    background: var(--wrong-bg, #e74c3c);
    border-color: var(--wrong-border, #c0392b);
    color: white;
    animation: tileShake 0.4s ease;
  }

  .tile-selected {
    background: var(--selected-bg, #f39c12);
    border-color: var(--selected-border, #e67e22);
    color: white;
  }

  /* Sizes */
  .tile-small {
    width: 32px;
    height: 32px;
    font-size: 14px;
    border-radius: 4px;
  }

  .tile-medium {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .tile-large {
    width: 56px;
    height: 56px;
    font-size: 22px;
    border-radius: 8px;
  }

  @keyframes tilePop {
    0% { transform: scale(0.8); }
    50% { transform: scale(1.15); }
    100% { transform: scale(1); }
  }

  @keyframes tileShake {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-6px); }
    40% { transform: translateX(6px); }
    60% { transform: translateX(-4px); }
    80% { transform: translateX(4px); }
  }
</style>
